(() => {
  const inputSelector = '[data-monoree-ring-catalog-input]';
  const configSelector = '[data-monoree-ring-catalog-config]';
  const measurementParam = 'monoree_size';
  const availabilityParam = 'filter.v.availability';
  const emptySize = '__monoree_no_ring_size__';
  const cache = new Map();
  let timer;
  let controller;
  let generation = 0;
  let typedValue = '';
  let caret;
  let lastCatalogInputAt = -Infinity;
  let pendingNativeInput;
  let draftActive = false;

  const core = () => window.MonoreeRingSizing;
  const parseJSON = (element) => {
    try { return JSON.parse(element?.textContent || 'null'); } catch { return null; }
  };
  const config = () => parseJSON(document.querySelector(configSelector));
  const paramsNow = () => new URLSearchParams(window.location.search);
  const canonical = (size) => String(size / 100);
  const displaySize = (size) => new Intl.NumberFormat(document.documentElement.lang || 'lt', {
    maximumFractionDigits: 2,
  }).format(size / 100);
  const preservePreview = (params) => {
    const preview = paramsNow().get('preview_theme_id');
    if (preview && !params.has('preview_theme_id')) params.set('preview_theme_id', preview);
    return params;
  };
  const currentFormParams = () => {
    const params = paramsNow();
    const form = pendingNativeInput?.target?.closest('form');
    if (!form?.isConnected) return params;
    const mobile = ['FacetFiltersFormMobile', 'FacetSortFormMobile'];
    const ids = mobile.includes(form.id) ? mobile : ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'];
    [...params.keys()].filter((name) => name.startsWith('filter.') || name === 'sort_by')
      .forEach((name) => params.delete(name));
    ids.forEach((id) => {
      const source = document.getElementById(id);
      if (!source) return;
      new FormData(source).forEach((value, name) => params.append(name, value));
    });
    return params;
  };
  const cancelPending = () => {
    generation += 1;
    window.clearTimeout(timer);
    controller?.abort();
    controller = null;
  };
  const setStatus = (kind) => {
    document.querySelectorAll('[data-monoree-ring-catalog]').forEach((finder) => {
      const strings = parseJSON(finder.querySelector(configSelector));
      const status = finder.querySelector('[data-monoree-ring-catalog-status]');
      const input = finder.querySelector(inputSelector);
      if (!status || !input) return;
      status.textContent = strings?.[kind] || '';
      status.hidden = !status.textContent;
      input.setAttribute('aria-invalid', String(kind === 'invalid'));
      finder.setAttribute('aria-busy', String(kind === 'loading'));
    });
  };
  const renderPage = (params, event) => {
    const query = preservePreview(params).toString();
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.renderPage(query, event);
    } else {
      window.location.assign(`${window.location.pathname}?${query}`);
    }
  };
  const renderCards = (target) => {
    document.querySelectorAll('[data-monoree-ring-card]').forEach((card) => {
      const data = parseJSON(card.querySelector('[data-monoree-ring-card-data]'));
      const links = card.querySelector('[data-monoree-ring-card-links]');
      if (!links) return;
      links.replaceChildren();
      const matches = target === null || !data ? [] : core().nearest(data.variants, target, {
        sizeIndex: data.sizeIndex, matchedOnly: true,
      });
      const seenSizes = new Set();
      matches.forEach((variant) => {
        const size = core().parseSize(variant.options[data.sizeIndex]);
        if (seenSizes.has(size) || seenSizes.size >= 2) return;
        seenSizes.add(size);
        const link = document.createElement('a');
        const url = new URL(variant.url, window.location.origin);
        const preview = paramsNow().get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        link.href = `${url.pathname}${url.search}`;
        link.className = 'monoree-ring-choice';
        const otherOptions = variant.options.filter((_, index) => index !== data.sizeIndex);
        link.textContent = `${displaySize(size)} mm${otherOptions.length ? ` · ${otherOptions.join(' / ')}` : ''}`;
        if (links.childNodes.length) links.append(document.createTextNode(' · '));
        links.append(link);
      });
      card.hidden = matches.length === 0;
    });
  };
  const refresh = (query = window.location.search) => {
    if (!core()) return;
    const params = new URLSearchParams(query);
    const target = core().parseSize(params.get(measurementParam));
    const typedTarget = core().parseSize(typedValue);
    if (target === typedTarget && (target !== null || !typedValue.trim())) draftActive = false;
    document.querySelectorAll(inputSelector).forEach((input) => {
      input.value = draftActive ? typedValue : target === null ? '' : typedTarget === target ? typedValue : displaySize(target);
      if (caret?.id === input.id && document.activeElement === input) {
        input.setSelectionRange(caret.start, caret.end);
      }
    });
    renderCards(target);
    const count = Number(document.getElementById('ProductCount')?.dataset.productCount);
    setStatus(draftActive && typedValue.trim() && typedTarget === null ? 'invalid' :
      controller && !controller.signal.aborted ? 'loading' : target !== null && count === 0 ? 'empty' : '');
    document.querySelectorAll('.pagination-wrapper a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (target === null) url.searchParams.delete(measurementParam);
      else url.searchParams.set(measurementParam, canonical(target));
      preservePreview(url.searchParams);
      link.href = `${url.pathname}${url.search}${url.hash}`;
    });
  };
  const getValues = async (params, sizeParam, signal) => {
    const section = document.getElementById('product-grid')?.dataset.id;
    if (!section) throw new Error('Missing collection section');
    const base = new URLSearchParams(params);
    [...base.keys()].filter((name) => name.startsWith('filter.')).forEach((name) => base.delete(name));
    base.delete(measurementParam);
    base.delete('page');
    base.delete('sort_by');
    base.set(availabilityParam, '1');
    base.set('section_id', section);
    preservePreview(base);
    const key = `${window.location.pathname}?${base.toString()}`;
    const saved = cache.get(key);
    if (saved && Date.now() - saved.time < 30000) return saved.values;
    const response = await fetch(key, { signal });
    if (!response.ok) throw new Error(`Collection lookup failed: ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (!html.getElementById('ProductGridContainer')) throw new Error('Invalid collection section');
    const values = Array.from(html.querySelectorAll(configSelector))
      .map(parseJSON).find((entry) => entry?.param === sizeParam)?.values || [];
    if (cache.size >= 8) cache.delete(cache.keys().next().value);
    cache.set(key, { values, time: Date.now() });
    return values;
  };
  const apply = async (value, input) => {
    cancelPending();
    const currentGeneration = generation;
    const settings = config();
    if (!settings || !core()) return;
    const hadPendingNativeInput = Boolean(pendingNativeInput?.target?.closest('form')?.isConnected);
    const params = currentFormParams();
    pendingNativeInput = null;
    const trimmed = value.trim();
    const event = input?.isConnected ? { target: input } : undefined;
    if (!trimmed) {
      params.delete(measurementParam);
      params.delete(settings.param);
      params.delete('page');
      renderPage(params, event);
      return;
    }
    const target = core().parseSize(trimmed);
    if (target === null) {
      setStatus('invalid');
      if (hadPendingNativeInput) {
        params.delete('page');
        renderPage(params, event);
      }
      return;
    }
    controller = new AbortController();
    const signal = controller.signal;
    setStatus('loading');
    try {
      const values = await getValues(params, settings.param, signal);
      if (signal.aborted || currentGeneration !== generation) return;
      const matches = [...new Set(values.filter((value) => {
        const size = core().parseSize(value);
        return size !== null && Math.abs(size - target) <= 30;
      }))];
      params.delete(settings.param);
      (matches.length ? matches : [emptySize]).forEach((value) => params.append(settings.param, value));
      params.set(availabilityParam, '1');
      params.set(measurementParam, canonical(target));
      params.delete('page');
      renderPage(params, event);
    } catch (error) {
      if (signal.aborted || currentGeneration !== generation || error.name === 'AbortError') return;
      setStatus('error');
    }
  };
  const formParams = (query, event) => {
    cancelPending();
    pendingNativeInput = null;
    draftActive = false;
    const params = preservePreview(new URLSearchParams(query));
    const settings = config();
    if (!settings) return params.toString();
    const changedName = event?.target?.name;
    const target = core()?.parseSize(paramsNow().get(measurementParam));
    if (target !== null && target !== undefined && changedName !== settings.param && changedName !== availabilityParam) {
      params.set(measurementParam, canonical(target));
      params.set(availabilityParam, '1');
      params.delete(settings.param);
      paramsNow().getAll(settings.param).forEach((value) => params.append(settings.param, value));
    } else {
      params.delete(measurementParam);
    }
    return params.toString();
  };
  const removalParams = (query) => {
    cancelPending();
    pendingNativeInput = null;
    draftActive = false;
    const params = preservePreview(new URLSearchParams(query));
    const current = paramsNow();
    const settings = config();
    if (!settings) return params.toString();
    const sameValues = (name) => JSON.stringify(params.getAll(name).sort()) === JSON.stringify(current.getAll(name).sort());
    const target = core()?.parseSize(current.get(measurementParam));
    if (target !== null && target !== undefined && sameValues(settings.param) && sameValues(availabilityParam)) {
      params.set(measurementParam, canonical(target));
    } else {
      params.delete(measurementParam);
    }
    return params.toString();
  };
  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!input.matches(inputSelector)) {
      if (input.closest('facet-filters-form')) {
        pendingNativeInput = event;
        cancelPending();
      }
      return;
    }
    event.stopPropagation();
    lastCatalogInputAt = event.timeStamp;
    cancelPending();
    document.querySelectorAll(inputSelector).forEach((other) => { if (other !== input) other.value = input.value; });
    const value = input.value;
    typedValue = value;
    draftActive = true;
    caret = { id: input.id, start: input.selectionStart, end: input.selectionEnd };
    setStatus(value.trim() && core()?.parseSize(value) === null ? 'invalid' : '');
    timer = window.setTimeout(() => apply(value, input), 350);
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches(inputSelector)) return;
    event.preventDefault();
    event.stopPropagation();
    lastCatalogInputAt = event.timeStamp;
    apply(event.target.value, event.target);
  }, true);
  window.addEventListener('popstate', () => { cancelPending(); draftActive = false; typedValue = ''; });
  const init = () => {
    refresh();
    const settings = config();
    const params = paramsNow();
    const target = core()?.parseSize(params.get(measurementParam));
    if (settings && target !== null && target !== undefined &&
      (!params.has(settings.param) || params.get(availabilityParam) !== '1')) {
      apply(canonical(target), document.querySelector(inputSelector));
    }
  };
  window.MonoreeRingCatalog = Object.freeze({
    refresh, formParams, removalParams,
    beforeRender: () => { cancelPending(); pendingNativeInput = null; },
    allowFormSubmit: (event) => !event || event.timeStamp >= lastCatalogInputAt,
  });
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
